import { useState, useEffect, useRef, useCallback } from 'react'
import config from '@/lib/config'
import { formatCoinsLabel } from '@/lib/coins'

function getGuestId() {
  let id = localStorage.getItem('tv_guest_id')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('tv_guest_id', id)
  }
  return id
}

function arcadeHeaders(playMode) {
  const headers = { 'Content-Type': 'application/json', 'X-Play-Mode': playMode }
  if (playMode === 'demo') headers['X-Guest-ID'] = getGuestId()
  return headers
}

function arcadeFetch(playMode, path, body) {
  return fetch(`${config.backendUrl}/arcade/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: arcadeHeaders(playMode),
    body: JSON.stringify(body),
  }).then(r => r.json())
}

function calcMultiplier(elapsed) {
  return parseFloat(Math.pow(1.08, elapsed / 0.1).toFixed(2))
}

export default function TvCrash({ onBalanceChange, playMode = 'demo' }) {
  const [balance, setBalance] = useState(null)
  const [bet, setBet] = useState('1.00')
  const [phase, setPhase] = useState('idle')
  const [displayMult, setDisplayMult] = useState(1.00)
  const [crashPoint, setCrashPoint] = useState(null)
  const [cashoutMult, setCashoutMult] = useState(null)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [autoCashout, setAutoCashout] = useState('')

  const rafRef = useRef(null)
  const startTimeRef = useRef(null)
  const gameIdRef = useRef(null)
  const phaseRef = useRef('idle')
  const autoCashoutRef = useRef('')

  useEffect(() => {
    setMsg('')
    fetch(`${config.backendUrl}/arcade/balance`, {
      credentials: 'include',
      headers: arcadeHeaders(playMode),
    }).then(r => r.json()).then(d => {
      if (d.success) setBalance(d.data.balance)
      else if (playMode === 'account') setMsg('Log in to play with your saved balance, or use Free Play.')
    }).catch(() => {})
  }, [playMode])

  const updateBalance = useCallback((b) => {
    setBalance(b)
    onBalanceChange?.(b)
  }, [onBalanceChange])

  const stopAnim = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const doCashout = useCallback(async (mult) => {
    if (phaseRef.current !== 'playing') return
    phaseRef.current = 'cashing'
    stopAnim()

    const res = await arcadeFetch(playMode, 'crash/cashout', { gameId: gameIdRef.current, cashoutAt: mult })
    if (!res.success) {
      setMsg(res.error)
      phaseRef.current = 'idle'
      setPhase('idle')
      return
    }

    updateBalance(res.data.balance)
    setCrashPoint(res.data.crashPoint)
    setCashoutMult(res.data.cashoutAt)

    if (res.data.win) {
      setMsg(`Cashed out at ${res.data.cashoutAt}x - won ${formatCoinsLabel(res.data.payout)}.`)
      setPhase('won')
      phaseRef.current = 'won'
    } else {
      setDisplayMult(res.data.crashPoint)
      setMsg(`Crashed at ${res.data.crashPoint}x - you were too late.`)
      setPhase('crashed')
      phaseRef.current = 'crashed'
    }
  }, [playMode, stopAnim, updateBalance])

  const startAnim = useCallback(() => {
    startTimeRef.current = performance.now()

    const tick = (now) => {
      if (phaseRef.current !== 'playing') return

      const elapsed = (now - startTimeRef.current) / 1000
      const mult = calcMultiplier(elapsed)
      setDisplayMult(mult)

      const auto = parseFloat(autoCashoutRef.current)
      if (!isNaN(auto) && auto >= 1.01 && mult >= auto) {
        doCashout(parseFloat(mult.toFixed(2)))
        return
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [doCashout])

  const startGame = async () => {
    setLoading(true)
    setMsg('')
    const betNum = parseFloat(bet)
    if (isNaN(betNum) || betNum < 0.01) {
      setMsg('Invalid bet')
      setLoading(false)
      return
    }

    const res = await arcadeFetch(playMode, 'crash/bet', { bet: betNum })
    if (!res.success) {
      setMsg(playMode === 'account' && res.error === 'Not authenticated'
        ? 'Log in to play with your saved balance, or use Free Play.'
        : res.error)
      setLoading(false)
      return
    }

    gameIdRef.current = res.data.gameId
    autoCashoutRef.current = autoCashout
    updateBalance(res.data.balance)
    setCrashPoint(null)
    setCashoutMult(null)
    setDisplayMult(1.00)
    phaseRef.current = 'playing'
    setPhase('playing')
    setLoading(false)
    startAnim()
  }

  const handleCashout = () => {
    if (phase !== 'playing' || loading) return
    doCashout(parseFloat(displayMult.toFixed(2)))
  }

  const reset = () => {
    stopAnim()
    phaseRef.current = 'idle'
    setPhase('idle')
    setDisplayMult(1.00)
    setCrashPoint(null)
    setCashoutMult(null)
    setMsg('')
  }

  const multColor = phase === 'crashed' ? 'text-red-400'
    : phase === 'won' ? 'text-green-400'
    : displayMult >= 2 ? 'text-green-300'
    : displayMult >= 1.5 ? 'text-yellow-300'
    : 'text-yellow-400'

  return (
    <div className="w-full max-w-2xl mx-auto select-none">
      <h1 className="text-yellow-400 text-2xl font-bold mb-2 text-center tracking-wider">CRASH</h1>
      <div className="text-center text-gray-400 text-sm mb-4">
        {playMode === 'demo' ? 'Free Play' : 'Saved Balance'}
      </div>

      {balance !== null && (
        <div className="text-center text-gray-400 text-sm mb-4">
          Balance: <span className="text-yellow-400 font-bold">{formatCoinsLabel(balance)}</span>
        </div>
      )}

      <div className="bg-[#0f1117] rounded-2xl flex items-center justify-center mb-4" style={{ height: '200px' }}>
        {phase === 'idle' ? (
          <div className="text-gray-500 text-lg">Place a bet to start</div>
        ) : (
          <div className="text-center">
            <div className={`text-6xl font-black ${multColor} transition-colors`} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {displayMult.toFixed(2)}x
            </div>
            {(phase === 'crashed' || phase === 'won') && crashPoint && (
              <div className="text-gray-500 text-sm mt-2">
                Crashed at {crashPoint}x
                {cashoutMult && cashoutMult !== crashPoint && ` - You cashed out at ${cashoutMult}x`}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#1a1d29] rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-gray-400 text-xs uppercase tracking-wide">Bet (Coins)</label>
          <input
            type="number" min="0.01" step="0.01" value={bet}
            onChange={e => setBet(e.target.value)}
            disabled={phase === 'playing'}
            className="bg-[#0f1117] text-white border border-gray-700 rounded-lg px-3 py-2 w-28 text-sm focus:border-yellow-500 focus:outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-gray-400 text-xs uppercase tracking-wide">Auto cashout at</label>
          <input
            type="number" min="1.01" step="0.1" placeholder="e.g. 2.00"
            value={autoCashout}
            onChange={e => { setAutoCashout(e.target.value); autoCashoutRef.current = e.target.value }}
            disabled={phase === 'playing'}
            className="bg-[#0f1117] text-white border border-gray-700 rounded-lg px-3 py-2 w-28 text-sm focus:border-yellow-500 focus:outline-none disabled:opacity-50"
          />
        </div>
        <div className="ml-auto flex gap-2">
          {phase === 'idle' || phase === 'crashed' || phase === 'won' ? (
            <button
              onClick={phase === 'idle' ? startGame : reset}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {loading ? '...' : phase === 'idle' ? 'Bet' : 'Play Again'}
            </button>
          ) : (
            <button
              onClick={handleCashout}
              disabled={loading}
              className="bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 animate-pulse"
            >
              Cash Out {displayMult.toFixed(2)}x
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div className={`text-center text-sm font-medium ${phase === 'won' ? 'text-green-400' : phase === 'crashed' ? 'text-red-400' : 'text-gray-300'}`}>
          {msg}
        </div>
      )}

      <p className="text-center text-xs text-gray-600 mt-4">
        {playMode === 'demo' ? 'Free-play coins only. No cash value.' : 'Playing with your saved coin balance.'}
      </p>
    </div>
  )
}
